import { Fragment } from 'react';
import { Divider, Flex, Progress, Typography, theme } from 'antd';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import { PlatformIconEcho } from '@/components/PlatformIconEcho';
import { useFilterContext } from '@/context';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useProfilesQuery } from '@/services/revenueApi';
import { isSocialPlatform, Platform, socialPlatforms } from '@/types/api/platforms';
import { getDateString } from '@/utils/dateUtils/dateFormatUtils';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import type { ProfilesResponse } from '@/types/api/profile';

type Profile = ProfilesResponse['profiles'][number];

interface FollowersCardEchoProps {
  className?: string;
}

interface FollowingRow {
  platform: Platform;
  label: string;
  count: number;
}

/**
 * Combined social following card for YouTube, Instagram, and TikTok.
 */
const FollowersCardEcho = ({ className }: FollowersCardEchoProps): JSX.Element | null => {
  const { filters } = useFilterContext();

  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0,
  });

  const socialProfiles =
    queryResult.data?.profiles.filter((profile) => isSocialPlatform(profile.platform)) ?? [];

  if (!queryResult.isPending && !queryResult.isError && socialProfiles.length === 0) {
    return null;
  }

  return (
    <CardShellEcho className={className} title="Following">
      <CardContent
        variant="echo"
        {...queryResult}
        data={socialProfiles}
        renderData={(profiles: Profile[]) => <FollowingRows profiles={profiles} />}
      />
    </CardShellEcho>
  );
};

const getFollowingCount = (profile: Profile): number => {
  if (profile.platform === Platform.YouTube && profile.subscribers != null) {
    return profile.subscribers;
  }

  return profile.followers ?? profile.subscribers ?? 0;
};

const getFollowingLabel = (profile: Profile): string =>
  profile.subscribers != null && profile.platform === Platform.YouTube
    ? 'Subscribers'
    : 'Followers';

const FollowingRows = ({ profiles }: { profiles: Profile[] }): JSX.Element => {
  const { token } = theme.useToken();

  const rows: FollowingRow[] = socialPlatforms.flatMap((platform) => {
    const profile = profiles.find((socialProfile) => socialProfile.platform === platform);
    if (!profile) {
      return [];
    }

    return [
      {
        platform,
        label: getFollowingLabel(profile),
        count: getFollowingCount(profile),
      },
    ];
  });

  const maxCount = Math.max(...rows.map((row) => row.count), 0);
  // TODO: re-enable selected date range when we support followers over time
  const dateString = getDateString(new Date().toISOString().split('T')[0]);

  return (
    <Flex vertical data-testid="followers-card-data">
      {rows.map((row, rowIndex) => {
        const percent = maxCount > 0 ? (row.count / maxCount) * 100 : 0;

        return (
          <Fragment key={row.platform}>
            {rowIndex > 0 && <Divider style={{ margin: 0 }} />}
            <Flex vertical style={{ paddingBlock: token.padding }}>
              <Flex
                align="center"
                gap={token.marginXXS}
                style={{ marginBottom: token.marginXXS }}
              >
                <Typography.Text
                  style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}
                >
                  {row.label} {row.platform}
                </Typography.Text>
                <PlatformIconEcho name={row.platform} />
              </Flex>
              <Flex align="center" gap={token.marginLG}>
                <Typography.Title level={3} style={{ margin: 0, flexShrink: 0 }}>
                  {formatBigNumbers(row.count)}
                </Typography.Title>
                <Progress
                  percent={percent}
                  showInfo={false}
                  strokeColor={token.colorPrimary}
                  railColor={token.colorFillSecondary}
                  size={{ height: 8 }}
                  style={{ margin: 0, flex: 1 }}
                />
              </Flex>
              <Typography.Text
                style={{
                  color: token.colorTextDescription,
                  fontSize: token.fontSizeSM,
                }}
              >
                {dateString}
              </Typography.Text>
            </Flex>
          </Fragment>
        );
      })}
    </Flex>
  );
};

export default FollowersCardEcho;
